// components/MultiPlatformPixelScript/hooks.ts — Pixel init effect (deferred, retry-based)
// Guard: sessionStorage per orderId mencegah double-fire saat remount / React Strict Mode.
import { useEffect, useRef } from 'react';
import type { FireContext, MultiPlatformPixelScriptProps } from './types';
import { isEmbedded } from '../../utils/embedTracking';
import { createFireWithRetry } from './utils';
import { waitForContentReady } from '../../utils/pixelGate';
import { fireMetaPixels } from './metaPixel';
import { fireGooglePixels } from './googlePixel';
import { fireTiktokPixels } from './tiktokPixel';
import { fireSnackPixels } from './snackPixel';
import { fireOpenAIPixels } from './openaiPixel';

export function useMultiPlatformPixels(props: MultiPlatformPixelScriptProps): void {
    const { configs, contentName, contentIds, pageType, orderData, value, userData, embedMode = 'smart', eventId } = props;
    const embeddedRef = useRef<boolean>(isEmbedded());
    const logPrefix = pageType === 'thankYouPage' ? 'Thank You' : 'Form Page';
    const hasFiredRef = useRef<Set<string>>(new Set());
    const pixelInitializedRef = useRef(false);
    const orderDataStr = orderData ? JSON.stringify(orderData) : null;
    const contentIdsStr = contentIds ? JSON.stringify(contentIds) : null;
    const userDataStr = userData ? JSON.stringify(userData) : null;
    const configsStr = JSON.stringify(configs);
    const timeoutIdsRef = useRef<NodeJS.Timeout[]>([]);
    const deferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Session-scoped dedup key: per orderId (ThankYou) atau per pageType+event
    // (form). Event masuk key agar tahap funnel beda (VC vs IC vs AddPaymentInfo)
    // tidak saling menekan — tiap tahap tetap 1x per sesi (anti StrictMode/remount).
    const eventSuffix = (configs || []).map(c => c.eventName).join('+');
    const sessionKey = pageType === 'thankYouPage' && orderData?.orderId
        ? `px_fired_order_${orderData.orderId}`
        : `px_fired_${pageType}_${contentName}_${eventSuffix}`;

    useEffect(() => {
        if (pixelInitializedRef.current) return;
        try {
            if (sessionStorage.getItem(sessionKey)) {
                console.debug(`[${logPrefix} Pixel] skip (already fired this session): ${sessionKey}`);
                return;
            }
        } catch { /* iframe sandbox: storage ditolak, lanjut fire tanpa dedup */ }

        const deferInit = () => {
            if (pixelInitializedRef.current) return;
            try { if (sessionStorage.getItem(sessionKey)) return; } catch { /* abaikan */ }

            console.debug(`[${logPrefix} Pixel] === MOUNT ===`);
            if (!configs || configs.length === 0) {
                console.warn(`[${logPrefix}] Tidak ada pixel config - tracking disabled`);
                return;
            }
            pixelInitializedRef.current = true;
            try { sessionStorage.setItem(sessionKey, '1'); } catch { /* abaikan */ }
            const fireWithRetry = createFireWithRetry({ logPrefix, pageType, orderData, contentName, configs, hasFiredRef, timeoutIdsRef });
            const ctx: FireContext = { logPrefix, pageType, orderData, contentName, contentIds, value, userData, eventId, configs, hasFiredRef, timeoutIdsRef, fireWithRetry, embedded: embeddedRef.current, embedMode };
            fireMetaPixels(ctx);
            fireGooglePixels(ctx);
            fireTiktokPixels(ctx);
            fireSnackPixels(ctx);
            fireOpenAIPixels(ctx);
        };

        // GATE sukses-load: pixel observasi fire SETELAH konten ter-render
        // (ganti timeout-buta 2000ms). Timeout aman 5 dtk di dalam gate.
        let cancelled = false;
        void waitForContentReady().then(() => {
            if (!cancelled) deferInit();
        });

        return () => {
            cancelled = true;
            if (deferTimerRef.current !== null) { clearTimeout(deferTimerRef.current); deferTimerRef.current = null; }
            timeoutIdsRef.current.forEach(id => clearTimeout(id));
            timeoutIdsRef.current = [];
            // TIDAK reset pixelInitializedRef — cegah re-fire saat React Strict Mode unmount/remount.
        };
    }, [configsStr, contentName, contentIdsStr, pageType, orderDataStr, userDataStr, sessionKey, embedMode]);
}
